import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_TRIALFORM_LOAD,
    ACTION_TRIALFORM_LOAD_CURRENT_USER,
    ACTION_TRIALFORM_LOAD_BY_ID,
    ACTION_TRIALFORM_LOAD_FAILURE,
    ACTION_TRIALFORM_LOAD_SUCCESS,

    ACTION_TRIALFORM_SAVE,
    ACTION_TRIALFORM_SAVE_SUCCESS,
    ACTION_TRIALFORM_SAVE_FAILURE,

    ACTION_TRIALFORM_CREATE,
    ACTION_TRIALFORM_CREATE_SUCCESS,
    ACTION_TRIALFORM_CREATE_FAILURE,

    ACTION_TRIALFORM_CREATEVERSION,
    ACTION_TRIALFORM_CREATEVERSION_SUCCESS,
    ACTION_TRIALFORM_CREATEVERSION_FAILURE,

    ACTION_TRIALFORM_CLEAR,
    ACTION_TRIALFORM_FILTER,

    ACTION_TRIALFORM_REPORT_LOAD,
    ACTION_TRIALFORM_REPORT_LOAD_SUCCESS,
    ACTION_TRIALFORM_REPORT_LOAD_FAILURE,

    ACTION_TRIALFORM_REPORT_JUSTIFIED,
    ACTION_TRIALFORM_REPORT_JUSTIFIED_SUCCESS,
    ACTION_TRIALFORM_REPORT_JUSTIFIED_FAILURE,


    TrialFormAction
} from '../actions/trialForm';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ITrialFormState {
    formData: Dtos.TrialForm | Dtos.TrialForm[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    formRoles: Dtos.RoleType[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.TrialForm[];
    formLength: number;
    reportData: Dtos.MeasurementReport[];
    reportLoadState: IRequestState;
    justifyingState: IRequestState;
}

const initialState: ITrialFormState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    formRoles: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0,
    reportData: undefined,
    reportLoadState: {
        status: RequestState.None
    } as IRequestState,
    justifyingState: {
        status: RequestState.None
    } as IRequestState,
};

function loading(state: ITrialFormState, isLoading: boolean): ITrialFormState {
    const newState: ITrialFormState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: ITrialFormState,
    formData: Dtos.TrialForm | Dtos.TrialForm[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    formRoles: Dtos.RoleType[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ITrialFormState {
    const newState: ITrialFormState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        formRoles: {
            $set: formRoles
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus?.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus?.message : undefined,
                errors: responseStatus ? responseStatus?.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus?.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: ITrialFormState, responseStatus: Dtos.ResponseStatus): ITrialFormState {
    const newState: ITrialFormState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: ITrialFormState, formFilter: IFormFilter): ITrialFormState {

    const items: Dtos.TrialForm[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" ||
            p.formTemplate.name.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: ITrialFormState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: ITrialFormState, propertiesToClear?: string[]): ITrialFormState {

    if (!propertiesToClear) {
        const newState: ITrialFormState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ITrialFormState, isSaving: boolean): ITrialFormState {
    const newState: ITrialFormState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ITrialFormState,
    formData: Dtos.TrialForm | Dtos.TrialForm[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialFormState {
    const newState: ITrialFormState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus?.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus?.message : undefined,
                errors: responseStatus ? responseStatus?.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus?.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ITrialFormState, responseStatus: Dtos.ResponseStatus): ITrialFormState {
    const newState: ITrialFormState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus?.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ITrialFormState, isCreating: boolean): ITrialFormState {
    const status = isCreating ? RequestState.Pending : RequestState.Success;
    const newState: ITrialFormState = update(state, {
        createState: {
            $set: {
                status: status
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ITrialFormState,
    formData: Dtos.TrialForm,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    formRoles: Dtos.RoleType[],
    lookups: Dtos.Lookup[]
): ITrialFormState {
    const newState: ITrialFormState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        formRoles: {
            $set: formRoles
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ITrialFormState, responseStatus: Dtos.ResponseStatus): ITrialFormState {
    const newState: ITrialFormState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function reportLoading(state: ITrialFormState, isLoading: boolean): ITrialFormState {
    const newState: ITrialFormState = update(state, {
        reportLoadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function reportLoadSuccess(
    state: ITrialFormState,
    reportData: Dtos.MeasurementReport[],
    responseStatus: Dtos.ResponseStatus
): ITrialFormState {
    const newState: ITrialFormState = update(state, {
        reportData: {
            $set: reportData
        },
        reportLoadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus?.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus?.message : undefined,
                errors: responseStatus ? responseStatus?.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        }
    });

    return newState;
}

function reportLoadFailure(state: ITrialFormState, responseStatus: Dtos.ResponseStatus): ITrialFormState {
    const newState: ITrialFormState = update(state, {
        reportLoadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function justifying(state: ITrialFormState, isSaving: boolean): ITrialFormState {
    const newState: ITrialFormState = update(state, {
        justifyingState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function justifySuccess(
    state: ITrialFormState,
    reportData: Dtos.MeasurementReport[],
    responseStatus: Dtos.ResponseStatus
): ITrialFormState {
    const newState: ITrialFormState = update(state, {
        reportData: {
            $set: reportData
        },
        justifyingState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus?.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus?.message : undefined,
                errors: responseStatus ? responseStatus?.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
    });

    return newState;
}

function justifyFailure(state: ITrialFormState, responseStatus: Dtos.ResponseStatus): ITrialFormState {
    const newState: ITrialFormState = update(state, {
        justifyingState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
    });

    return newState;
}


const TrialFormReducer: Reducer<ITrialFormState> = (state: ITrialFormState = initialState, action: TrialFormAction) => {
    switch (action.type) {
        case ACTION_TRIALFORM_LOAD:
        case ACTION_TRIALFORM_LOAD_CURRENT_USER:
        case ACTION_TRIALFORM_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_TRIALFORM_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.formRoles, action.lookups, action.responseStatus);
        case ACTION_TRIALFORM_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_TRIALFORM_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_TRIALFORM_SAVE:
            return saving(state, true);
        case ACTION_TRIALFORM_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALFORM_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_TRIALFORM_CREATEVERSION:
        case ACTION_TRIALFORM_CREATE:
            return creating(state, true);
        case ACTION_TRIALFORM_CREATEVERSION_SUCCESS:
            return creating(state, false);
        case ACTION_TRIALFORM_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.formRoles, action.lookups);
        case ACTION_TRIALFORM_CREATEVERSION_FAILURE:
        case ACTION_TRIALFORM_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_TRIALFORM_FILTER:
            return filter(state, action.formFilter);
        case ACTION_TRIALFORM_REPORT_LOAD:
            return reportLoading(state, true);
        case ACTION_TRIALFORM_REPORT_LOAD_SUCCESS:
            return reportLoadSuccess(state, action.formData, action.responseStatus);
        case ACTION_TRIALFORM_REPORT_LOAD_FAILURE:
            return reportLoadFailure(state, action.responseStatus);
        case ACTION_TRIALFORM_REPORT_JUSTIFIED:
            return justifying(state, true);
        case ACTION_TRIALFORM_REPORT_JUSTIFIED_SUCCESS:
            return justifySuccess(state, action.formData, action.responseStatus);
        case ACTION_TRIALFORM_REPORT_JUSTIFIED_FAILURE:
            return justifyFailure(state, action.responseStatus);

    }

    return state;
}

export default TrialFormReducer;